"use client";

import React from "react";

async function copyTextWithFallback(text) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
    }
  }

  if (typeof document === "undefined") {
    return false;
  }

  const textarea = document.createElement("textarea");
  const activeElement = document.activeElement;

  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  const copied = document.execCommand("copy");

  document.body.removeChild(textarea);

  if (activeElement instanceof HTMLElement) {
    activeElement.focus();
  }

  return copied;
}

function formatValue(value) {
  if (typeof value === "string") {
    return JSON.stringify(value);
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }

  return JSON.stringify(value, null, 2);
}

function formatSettingsForCopy(settings) {
  const lines = Object.entries(settings).map(
    ([key, value]) => `  ${key}: ${formatValue(value)}`,
  );

  return `const defaults = {\n${lines.join(",\n")}\n};`;
}

function PanelSection({ title, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <div className="rounded-[1.15rem] border border-black/6 bg-white/78 px-3 py-2.5">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <p className="text-[0.64rem] font-semibold uppercase tracking-[0.16em] text-[#06040c]/42">
          {title}
        </p>
        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-black/8 bg-[#f8f6ef] px-2 text-[0.68rem] font-semibold text-[#06040c]/58">
          {isOpen ? "Hide" : "Show"}
        </span>
      </button>
      {isOpen ? (
        <div className="space-y-2.5 pt-2.5 text-[0.75rem] text-[#06040c]/72">
          {children}
        </div>
      ) : null}
    </div>
  );
}

function SliderField({ label, value, min, max, step, onChange, className }) {
  return (
    <div>
      <label className="mb-1.5 block">
        {label}: {value}
      </label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(parseFloat(event.target.value))}
        className={className}
      />
    </div>
  );
}

function ToggleField({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-2xl border border-black/8 bg-[#f8f6ef] px-3 py-2">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div>
      <label className="mb-1 block">{label}</label>
      <input
        type="color"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 w-full cursor-pointer rounded-2xl border border-black/10 bg-white p-1"
      />
    </div>
  );
}

function NumberField({ label, value, min, max, onChange, className }) {
  return (
    <div>
      <label className="mb-1.5 block">{label}</label>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(parseFloat(event.target.value || value))}
        className={className}
      />
    </div>
  );
}

function SelectField({ label, value, options, onChange, className }) {
  return (
    <div>
      <label className="mb-1.5 block">{label}</label>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={className}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function GlobeTweakPanel({ settings, setSettings, defaults }) {
  const [isOpen, setIsOpen] = React.useState(true);
  const [copyState, setCopyState] = React.useState("idle");

  const update = (key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
  };

  const reset = () => setSettings({ ...(defaults || {}) });

  const copySettings = async () => {
    try {
      const copied = await copyTextWithFallback(
        formatSettingsForCopy(settings),
      );

      if (!copied) {
        throw new Error("Copy failed");
      }

      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1600);
    } catch {
      setCopyState("error");
      window.setTimeout(() => setCopyState("idle"), 1600);
    }
  };

  const sliderClassName =
    "h-1 w-full cursor-pointer appearance-none rounded-full bg-black/8 accent-[#06040c]";
  const fieldClassName =
    "w-full rounded-2xl border border-black/10 bg-white px-3 py-1.5 text-[0.75rem] text-[#06040c] shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] outline-none transition-colors duration-200 focus:border-black/18";

  return (
    <div className="fixed right-3 top-3 z-50 w-[min(18.5rem,calc(100%-1.5rem))]">
      <div className="max-h-[calc(100vh-1.5rem)] overflow-hidden rounded-[1.4rem] border border-black/8 bg-[#fbfbf8]/96 shadow-[0_24px_80px_rgba(6,4,12,0.12)] backdrop-blur-xl">
        <div className="flex items-start justify-between gap-3 px-3 py-3">
          <div>
            <p className="text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-[#06040c]/42">
              Work map controls
            </p>
            <strong className="font-raleway text-[0.92rem] font-semibold text-[#06040c]">
              Map tweaks
            </strong>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={copySettings}
              className="inline-flex h-8 items-center justify-center rounded-full border border-black/10 bg-white px-2.5 text-[0.72rem] font-semibold text-[#06040c]/72 transition-colors duration-200 hover:bg-black/3"
            >
              {copyState === "copied"
                ? "Copied"
                : copyState === "error"
                  ? "Failed"
                  : "Copy"}
            </button>

            <button
              type="button"
              onClick={() => setIsOpen((open) => !open)}
              className="inline-flex h-8 items-center justify-center rounded-full border border-black/10 bg-white px-2.5 text-[0.72rem] font-semibold text-[#06040c]/72 transition-colors duration-200 hover:bg-black/3"
            >
              {isOpen ? "Hide" : "Show"}
            </button>

            <button
              type="button"
              onClick={reset}
              className="inline-flex h-8 items-center justify-center rounded-full bg-[#06040c] px-3 text-[0.72rem] font-semibold text-white transition-colors duration-200 hover:bg-black"
            >
              Reset
            </button>
          </div>
        </div>

        {isOpen ? (
          <div className="max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-black/6 px-3 pb-3 pt-2.5">
            <div className="space-y-2">
              <PanelSection title="Canvas" defaultOpen>
                <NumberField
                  label="Min height"
                  value={settings.containerMinHeight}
                  min="320"
                  max="960"
                  onChange={(value) =>
                    update("containerMinHeight", Math.round(value))
                  }
                  className={fieldClassName}
                />
                <ColorField
                  label="Background"
                  value={settings.backgroundColor}
                  onChange={(value) => update("backgroundColor", value)}
                />
                <ColorField
                  label="Map dot colour"
                  value={settings.mapDotColor}
                  onChange={(value) => update("mapDotColor", value)}
                />
                <SliderField
                  label="Map dot opacity"
                  value={settings.mapDotOpacity}
                  min="0"
                  max="1"
                  step="0.01"
                  onChange={(value) => update("mapDotOpacity", value)}
                  className={sliderClassName}
                />
                <SliderField
                  label="Map dot radius"
                  value={settings.mapDotRadius}
                  min="0.08"
                  max="0.5"
                  step="0.01"
                  onChange={(value) => update("mapDotRadius", value)}
                  className={sliderClassName}
                />
                <ToggleField
                  label="Show glow"
                  checked={settings.showGlow}
                  onChange={(value) => update("showGlow", value)}
                />
                <ColorField
                  label="Glow colour"
                  value={settings.glowColor}
                  onChange={(value) => update("glowColor", value)}
                />
                <SliderField
                  label="Glow opacity"
                  value={settings.glowOpacity}
                  min="0"
                  max="0.5"
                  step="0.01"
                  onChange={(value) => update("glowOpacity", value)}
                  className={sliderClassName}
                />
              </PanelSection>

              <PanelSection title="Connections">
                <ColorField
                  label="Line colour"
                  value={settings.lineColor}
                  onChange={(value) => update("lineColor", value)}
                />
                <SliderField
                  label="Line opacity"
                  value={settings.lineOpacity}
                  min="0"
                  max="1"
                  step="0.01"
                  onChange={(value) => update("lineOpacity", value)}
                  className={sliderClassName}
                />
                <SliderField
                  label="Line width"
                  value={settings.lineWidth}
                  min="0.5"
                  max="4"
                  step="0.01"
                  onChange={(value) => update("lineWidth", value)}
                  className={sliderClassName}
                />
                <SliderField
                  label="Arc height"
                  value={settings.pathArcHeight}
                  min="0"
                  max="120"
                  step="1"
                  onChange={(value) => update("pathArcHeight", value)}
                  className={sliderClassName}
                />
                <SliderField
                  label="Draw duration"
                  value={settings.pathDuration}
                  min="0.2"
                  max="4"
                  step="0.01"
                  onChange={(value) => update("pathDuration", value)}
                  className={sliderClassName}
                />
                <SliderField
                  label="Delay step"
                  value={settings.pathDelayStep}
                  min="0"
                  max="0.6"
                  step="0.01"
                  onChange={(value) => update("pathDelayStep", value)}
                  className={sliderClassName}
                />
              </PanelSection>

              <PanelSection title="Endpoints">
                <ToggleField
                  label="Show endpoints"
                  checked={settings.showEndpoints}
                  onChange={(value) => update("showEndpoints", value)}
                />
                <ColorField
                  label="Endpoint colour"
                  value={settings.endpointColor}
                  onChange={(value) => update("endpointColor", value)}
                />
                <SliderField
                  label="Endpoint radius"
                  value={settings.endpointRadius}
                  min="1"
                  max="8"
                  step="0.1"
                  onChange={(value) => update("endpointRadius", value)}
                  className={sliderClassName}
                />
                <ToggleField
                  label="Show pulse"
                  checked={settings.showEndpointPulse}
                  onChange={(value) => update("showEndpointPulse", value)}
                />
                <ColorField
                  label="Pulse colour"
                  value={settings.endpointPulseColor}
                  onChange={(value) => update("endpointPulseColor", value)}
                />
                <SliderField
                  label="Pulse opacity"
                  value={settings.endpointPulseOpacity}
                  min="0"
                  max="1"
                  step="0.01"
                  onChange={(value) => update("endpointPulseOpacity", value)}
                  className={sliderClassName}
                />
                <SliderField
                  label="Pulse start"
                  value={settings.endpointPulseStartRadius}
                  min="1"
                  max="12"
                  step="0.1"
                  onChange={(value) =>
                    update("endpointPulseStartRadius", value)
                  }
                  className={sliderClassName}
                />
                <SliderField
                  label="Pulse end"
                  value={settings.endpointPulseEndRadius}
                  min="2"
                  max="20"
                  step="0.1"
                  onChange={(value) => update("endpointPulseEndRadius", value)}
                  className={sliderClassName}
                />
                <SliderField
                  label="Pulse duration"
                  value={settings.endpointPulseDuration}
                  min="0.4"
                  max="4"
                  step="0.01"
                  onChange={(value) => update("endpointPulseDuration", value)}
                  className={sliderClassName}
                />
              </PanelSection>

              <PanelSection title="Labels">
                <ToggleField
                  label="Show labels"
                  checked={settings.showLabels}
                  onChange={(value) => update("showLabels", value)}
                />
                <ToggleField
                  label="Animate labels"
                  checked={settings.showLabelAnimation}
                  onChange={(value) => update("showLabelAnimation", value)}
                />
                <SelectField
                  label="Animation mode"
                  value={settings.labelAnimationMode}
                  options={[
                    { label: "Left to right", value: "left-to-right" },
                    { label: "Random", value: "random" },
                    { label: "Mixed order", value: "mixed-order" },
                  ]}
                  onChange={(value) => update("labelAnimationMode", value)}
                  className={fieldClassName}
                />
                <SliderField
                  label="Step interval"
                  value={settings.labelAnimationInterval}
                  min="0.35"
                  max="3"
                  step="0.05"
                  onChange={(value) => update("labelAnimationInterval", value)}
                  className={sliderClassName}
                />
                <ToggleField
                  label="Avoid clashes"
                  checked={settings.labelCollisionMode === "avoid-overlap"}
                  onChange={(value) =>
                    update(
                      "labelCollisionMode",
                      value ? "avoid-overlap" : "fixed",
                    )
                  }
                />
                <SliderField
                  label="Label scale"
                  value={settings.labelScale}
                  min="0.4"
                  max="2.4"
                  step="0.05"
                  onChange={(value) => update("labelScale", value)}
                  className={sliderClassName}
                />
                <NumberField
                  label="Font size"
                  value={settings.labelFontSize}
                  min="8"
                  max="40"
                  onChange={(value) => update("labelFontSize", value)}
                  className={fieldClassName}
                />
                <SliderField
                  label="Vertical offset"
                  value={settings.labelOffsetY}
                  min="0"
                  max="48"
                  step="1"
                  onChange={(value) => update("labelOffsetY", value)}
                  className={sliderClassName}
                />
                <SliderField
                  label="Collision padding"
                  value={settings.labelCollisionPadding ?? 8}
                  min="0"
                  max="24"
                  step="1"
                  onChange={(value) => update("labelCollisionPadding", value)}
                  className={sliderClassName}
                />
                <ColorField
                  label="Label background"
                  value={settings.labelBackgroundColor}
                  onChange={(value) => update("labelBackgroundColor", value)}
                />
                <SliderField
                  label="Background opacity"
                  value={settings.labelBackgroundOpacity}
                  min="0"
                  max="1"
                  step="0.01"
                  onChange={(value) => update("labelBackgroundOpacity", value)}
                  className={sliderClassName}
                />
                <ColorField
                  label="Label text"
                  value={settings.labelTextColor}
                  onChange={(value) => update("labelTextColor", value)}
                />
                <ColorField
                  label="Label border"
                  value={settings.labelBorderColor}
                  onChange={(value) => update("labelBorderColor", value)}
                />
                <SliderField
                  label="Border opacity"
                  value={settings.labelBorderOpacity}
                  min="0"
                  max="1"
                  step="0.01"
                  onChange={(value) => update("labelBorderOpacity", value)}
                  className={sliderClassName}
                />
                <ColorField
                  label="Active background"
                  value={settings.labelActiveBackgroundColor}
                  onChange={(value) =>
                    update("labelActiveBackgroundColor", value)
                  }
                />
                <SliderField
                  label="Active bg opacity"
                  value={settings.labelActiveBackgroundOpacity}
                  min="0"
                  max="1"
                  step="0.01"
                  onChange={(value) =>
                    update("labelActiveBackgroundOpacity", value)
                  }
                  className={sliderClassName}
                />
                <ColorField
                  label="Active text"
                  value={settings.labelActiveTextColor}
                  onChange={(value) => update("labelActiveTextColor", value)}
                />
                <ColorField
                  label="Active border"
                  value={settings.labelActiveBorderColor}
                  onChange={(value) => update("labelActiveBorderColor", value)}
                />
                <SliderField
                  label="Active border opacity"
                  value={settings.labelActiveBorderOpacity}
                  min="0"
                  max="1"
                  step="0.01"
                  onChange={(value) =>
                    update("labelActiveBorderOpacity", value)
                  }
                  className={sliderClassName}
                />
                <ToggleField
                  label="Lift active label"
                  checked={settings.labelAnimateLift}
                  onChange={(value) => update("labelAnimateLift", value)}
                />
                <SliderField
                  label="Lift amount"
                  value={settings.labelActiveLift}
                  min="0"
                  max="18"
                  step="1"
                  onChange={(value) => update("labelActiveLift", value)}
                  className={sliderClassName}
                />
                <ToggleField
                  label="Expand active label"
                  checked={settings.labelAnimateScale}
                  onChange={(value) => update("labelAnimateScale", value)}
                />
                <SliderField
                  label="Active scale"
                  value={settings.labelActiveScale}
                  min="1"
                  max="1.4"
                  step="0.01"
                  onChange={(value) => update("labelActiveScale", value)}
                  className={sliderClassName}
                />
              </PanelSection>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
